<template>
  <div class="kpi-back-container">
    <Loading v-if="loading"></Loading>
    <template v-else>
      <div class="table-container" v-if="canView">
        <ve-table style="width: 100%;" :scroll-width="1800" :columns="columns" :table-data="tableData"
          row-key-field-name="rowKey" fixed-header max-height="100vh" :rowStyleOption="{ stripe: true }" />
      </div>
      <div v-else class="no-auth">
        <img src="https://file.iviewui.com/iview-pro/icon-403.svg">
        <p>抱歉，您访问的页面不存在</p>
      </div>
    </template>
  </div>
</template>

<script>
import DayJs from 'dayjs'
import { mapGetters } from 'vuex'
import Loading from '@/components/Loading'
import { formatMoney, hasOneOf } from '@/util'
import { fetchRoleCodeList } from '@/services/UserService'
import { getKpiBackStatistic } from '@/services/KpiService'
export default {
  components: {
    Loading
  },
  data() {
    return {
      loading: false,
      roleCodeList: [],
      tableData: []
    }
  },
  computed: {
    ...mapGetters([
      'achievementRate'
    ]),
    columns() {
      const achievementRate = parseInt(this.achievementRate) || 1
      return [
        {
          field: '',
          key: 'expand',
          title: '',
          width: 30,
          align: 'center',
          fixed: 'left',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return row.isLeaf ? '' : h('div', {
              style: {
                'font-size': '16px',
                position: 'relative',
                top: '-2px'
              },
              on: {
                click: () => {
                  this.onExpand(row, rowIndex)
                }
              }
            }, row.canExpand ? (row.isExpand ? '-' : '+') : '')
          }
        },
        {
          title: '姓名',
          key: 'vUserName',
          tree: true,
          fixed: 'left',
          align: 'left',
          width: 60,
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('span', {
              style: {
                position: 'relative',
                left: row.isLeaf ? '10px' : 0
              }
            }, row.vUserName || '待修正')
          }
        },
        {
          title: '客产(元)',
          key: 'capacityPerCustomer',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            const capacity = Number(row.capacityPerCustomer).toFixed(0)
            let color = ''
            if (capacity >= 2000) {
              color = '#19be6b'
            } else if (capacity >= 1500) {
              color = '#f90'
            } else {
              color = '#ed4014'
            }

            return h('div', {
              style: {
                color
              }
            }, [
              h('span', formatMoney(capacity))
            ])
          }
        },
        {
          title: '接待量',
          key: 'hasAgentCnt',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${row.hasAgentCnt * achievementRate}`)
          }
        },
        {
          title: '有方案/方案量',
          key: 'hasSchemeCnt',
          width: 100,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${row.hasSchemeCnt * achievementRate} / ${row.solutionCnt * achievementRate}`)
          }
        },
        {
          title: '审批中',
          key: 'reviewingCnt',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${row.reviewingCnt * achievementRate}`)
          }
        },
        {
          title: '通过',
          key: 'approvedCnt',
          width: 100,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${row.approvedCnt * achievementRate}`)
          }
        },
        {
          title: '放款',
          key: 'issuedCnt',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${row.issuedCnt * achievementRate}`)
          }
        },
        {
          title: '回款',
          key: 'commissionCnt',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${row.commissionCnt * achievementRate}`)
          }
        },
        {
          title: '批复金额(元)',
          key: 'approvedAmountTotal',
          width: 100,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', formatMoney(Number(row.approvedAmountTotal * achievementRate).toFixed(0)))
          }
        },
        {
          title: '放款金额(元)',
          key: 'issuedAmountTotal',
          width: 100,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', formatMoney(Number(row.issuedAmountTotal * achievementRate).toFixed(0)))
          }
        },
        {
          title: '待放金额(元)',
          key: 'waitIssuedAmountTotal',
          width: 100,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', formatMoney(Number((row.approvedAmountTotal - row.issuedAmountTotal) * achievementRate).toFixed(0)))
          }
        },
        {
          title: '方案率',
          key: 'vSchemeRate',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${Number(row.vSchemeRate * 100).toFixed(2)}%`)
          }
        },
        {
          title: '进件率',
          key: 'reviewingRate',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${Number(row.reviewingRate * 100).toFixed(2)}%`)
          }
        },
        {
          title: '批复率',
          key: 'approvedRate',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${Number(row.approvedRate * 100).toFixed(2)}%`)
          }
        },
        {
          title: '件均(元)',
          key: 'issuedAmountAvg',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', formatMoney(Number(row.issuedAmountAvg).toFixed(0)))
          }
        },
        {
          title: '实际点位',
          key: 'commissionPoint',
          width: 80,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', `${Number(row.commissionPoint * 100).toFixed(2)}%`)
          }
        },
        {
          title: '回款金额(元)',
          key: 'commissionAmountTotal',
          width: 100,
          align: 'center',
          renderBodyCell: ({ row, column, rowIndex }, h) => {
            return h('div', formatMoney(Number(row.commissionAmountTotal * achievementRate).toFixed(0)))
          }
        }
      ]
    },
    canView() {
      return this.roleCodeList && this.roleCodeList.length &&
        hasOneOf(this.roleCodeList, ['T', 'M', 'VL', 'V', 'L1'])
    }
  },
  async mounted() {
    document.title = '后端指标'
    const openId = localStorage.getItem('openId')
    this.loading = true
    await this.onInitRoleCodeList()
    const response = await getKpiBackStatistic({
      openId,
      startDate: DayJs().format('YYYY-MM-DD') + ' 00:00:00',
      endDate: DayJs().format('YYYY-MM-DD') + ' 23:59:59'
    })
    const kpiList = (response.data.kpiList || []).map(item => {
      if (item.vlUserId) {
        return {
          ...item,
          canExpand: true
        }
      }
      return {
        ...item,
        canExpand: false
      }
    })
    this.loading = false
    this.tableData = kpiList
  },
  methods: {
    async onInitRoleCodeList() {
      const openId = localStorage.getItem('openId')
      const response = await fetchRoleCodeList({ openId })
      this.roleCodeList = response.data || []
    },
    async onExpand(row, rowIndex) {
      const openId = localStorage.getItem('openId')
      if (!row.isExpand) {
        this.$set(row, 'isExpand', true)
        this.$toast.loading({
          duration: 0,
          message: '数据加载中...',
          forbidClick: true
        })
        const response = await getKpiBackStatistic({
          openId,
          startDate: DayJs().format('YYYY-MM-DD') + ' 00:00:00',
          endDate: DayJs().format('YYYY-MM-DD') + ' 23:59:59',
          userId: row.vlUserId
        })
        this.$toast.clear()
        const kpiList = (response.data.kpiList || []).map(item => ({
          ...item,
          isLeaf: true
        }))
        this.tableData.splice(rowIndex + 1, 0, ...kpiList)
      } else {
        for (let index = rowIndex + 1; index < this.tableData.length; index++) {
          if (this.tableData[index].isLeaf) {
            this.tableData[index] = undefined
          } else {
            break
          }
        }
        this.$set(row, 'isExpand', false)
        this.tableData = this.tableData.filter(item => !!item)
      }
    }
  }
}
</script>
<style lang="stylus">
  .kpi-back-container {
    @media screen and (orientation portrait) {
      .table-container {
        .ve-table-header-tr, .ve-table-body-tr, .ve-table-expand-tr {
          height 30px !important
        }
        .ve-table-header-th, .ve-table-body-td, .ve-table-expand-td-content {
          font-size 12px !important
          padding 6px !important
        }
        .iconfont-vet {
          font-size 12px !important
        }
        .ve-table-row-expand-icon {
          width 15px !important
          height 15px !important
        }
      }
    }
    @media screen and (orientation landscape) {
      .table-container {
        .ve-table-header-tr, .ve-table-body-tr, .ve-table-expand-tr {
          height 20px !important
        }
        .ve-table-header-th, .ve-table-body-td, .ve-table-expand-td-content {
          font-size 8px !important
          padding 4px !important
        }
        .iconfont-vet {
          font-size 8px !important
        }
        .ve-table-row-expand-icon {
          width 10px !important
          height 10px !important
        }
      }
    }
    .no-auth {
      height 100vh
      display flex
      flex-direction column
      align-items center
      padding-top 100px
      img {
        width 80%
      }
      p {
        margin-top: 16px;
        color: #808695;
        font-size: 20px;
        line-height: 28px;
      }
    }
  }
</style>
